/**
 * Pure IndexedDB operations for offline storage.
 * No React dependencies -- can be used from hooks, workers, or service workers.
 */
import { logger } from '@/shared/utils/logger';

export type OfflineOperationType =
  | 'create_request'
  | 'update_request'
  | 'create_ra'
  | 'update_ra'
  | 'shop_product_delivery'
  | 'shop_product_return'
  | 'laundry_net_ritiro';

export interface OfflineOperation {
  id: string;
  type: OfflineOperationType;
  data: any;
  timestamp: number;
  attempts: number;
  lastAttempt?: number;
  module: string;
  optimisticId?: string;
  queryKeysToInvalidate?: string[][];
}

export interface OfflineStorageConfig {
  dbName: string;
  version: number;
  stores: {
    operations: string;
    raProfiles: string;
    clothingRequests: string;
    clothingStock: string;
    shopProducts: string;
    shopDeliveries: string;
    shopReturns: string;
    laundryMovements: string;
    preloadMetadata: string;
  };
}

// Configurazione database - Version 3 adds shop returns
export const DB_CONFIG: OfflineStorageConfig = {
  dbName: 'pasture-offline-db',
  version: 3,
  stores: {
    operations: 'pending-operations',
    raProfiles: 'ra-profiles-cache',
    clothingRequests: 'clothing-requests-cache',
    clothingStock: 'clothing-stock-cache',
    shopProducts: 'shop-products-cache',
    shopDeliveries: 'shop-deliveries-cache',
    shopReturns: 'shop-returns-cache',
    laundryMovements: 'laundry-movements-cache',
    preloadMetadata: 'preload-metadata'
  }
};

/** Open and initialize the offline IndexedDB database. */
export function openOfflineDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_CONFIG.dbName, DB_CONFIG.version);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      if (!db.objectStoreNames.contains(DB_CONFIG.stores.operations)) {
        const operationsStore = db.createObjectStore(DB_CONFIG.stores.operations, { keyPath: 'id' });
        operationsStore.createIndex('timestamp', 'timestamp');
        operationsStore.createIndex('type', 'type');
        operationsStore.createIndex('module', 'module');
      }

      if (!db.objectStoreNames.contains(DB_CONFIG.stores.raProfiles)) {
        const raStore = db.createObjectStore(DB_CONFIG.stores.raProfiles, { keyPath: 'id' });
        raStore.createIndex('simic', 'simic');
        raStore.createIndex('lastUpdated', 'lastUpdated');
      }

      if (!db.objectStoreNames.contains(DB_CONFIG.stores.clothingRequests)) {
        const requestsStore = db.createObjectStore(DB_CONFIG.stores.clothingRequests, { keyPath: 'id' });
        requestsStore.createIndex('status', 'status');
        requestsStore.createIndex('created_at', 'created_at');
        requestsStore.createIndex('ra_profile_id', 'ra_profile_id');
      }

      if (!db.objectStoreNames.contains(DB_CONFIG.stores.clothingStock)) {
        const stockStore = db.createObjectStore(DB_CONFIG.stores.clothingStock, { keyPath: 'id' });
        stockStore.createIndex('category', 'category');
        stockStore.createIndex('size', 'size');
      }

      if (!db.objectStoreNames.contains(DB_CONFIG.stores.shopProducts)) {
        const shopProductsStore = db.createObjectStore(DB_CONFIG.stores.shopProducts, { keyPath: 'id' });
        shopProductsStore.createIndex('category_id', 'category_id');
        shopProductsStore.createIndex('lastUpdated', 'lastUpdated');
      }

      if (!db.objectStoreNames.contains(DB_CONFIG.stores.shopDeliveries)) {
        const shopDeliveriesStore = db.createObjectStore(DB_CONFIG.stores.shopDeliveries, { keyPath: 'id' });
        shopDeliveriesStore.createIndex('ra_profile_id', 'ra_profile_id');
        shopDeliveriesStore.createIndex('delivery_date', 'delivery_date');
        shopDeliveriesStore.createIndex('lastUpdated', 'lastUpdated');
      }

      if (!db.objectStoreNames.contains(DB_CONFIG.stores.shopReturns)) {
        const shopReturnsStore = db.createObjectStore(DB_CONFIG.stores.shopReturns, { keyPath: 'id' });
        shopReturnsStore.createIndex('ra_profile_id', 'ra_profile_id');
        shopReturnsStore.createIndex('return_date', 'return_date');
        shopReturnsStore.createIndex('lastUpdated', 'lastUpdated');
      }

      if (!db.objectStoreNames.contains(DB_CONFIG.stores.laundryMovements)) {
        const laundryMovementsStore = db.createObjectStore(DB_CONFIG.stores.laundryMovements, { keyPath: 'id' });
        laundryMovementsStore.createIndex('net_id', 'net_id');
        laundryMovementsStore.createIndex('ra_profile_id', 'ra_profile_id');
        laundryMovementsStore.createIndex('data_movimento', 'data_movimento');
        laundryMovementsStore.createIndex('lastUpdated', 'lastUpdated');
      }

      if (!db.objectStoreNames.contains(DB_CONFIG.stores.preloadMetadata)) {
        db.createObjectStore(DB_CONFIG.stores.preloadMetadata, { keyPath: 'id' });
      }
    };
  });
}

/** Generic IDB operation helper. */
export function dbOperation<T>(
  db: IDBDatabase,
  storeName: string,
  mode: IDBTransactionMode,
  operation: (store: IDBObjectStore) => IDBRequest<T>
): Promise<T> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([storeName], mode);
    const store = transaction.objectStore(storeName);
    const request = operation(store);

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/** Put multiple items into a store, adding lastUpdated timestamp. */
export async function putItems(db: IDBDatabase, storeName: string, items: any[]): Promise<void> {
  const transaction = db.transaction([storeName], 'readwrite');
  const store = transaction.objectStore(storeName);

  for (const item of items) {
    await new Promise<void>((resolve, reject) => {
      const request = store.put({
        ...item,
        lastUpdated: Date.now(),
        cached: true
      });
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }
}

/** Clear a store and then put items into it. */
export async function replaceAll(db: IDBDatabase, storeName: string, items: any[]): Promise<void> {
  const transaction = db.transaction([storeName], 'readwrite');
  const store = transaction.objectStore(storeName);

  await new Promise<void>((resolve, reject) => {
    const clearRequest = store.clear();
    clearRequest.onsuccess = () => resolve();
    clearRequest.onerror = () => reject(clearRequest.error);
  });

  for (const item of items) {
    await new Promise<void>((resolve, reject) => {
      const request = store.put({
        ...item,
        lastUpdated: Date.now()
      });
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }
}
